import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../layout/dashboard/DashboardLayout';
import {
  formatFieldName,
  formatHistoryValue,
  getRelativeTime
} from '../../lib/historyUtils';
import { fetchHistory } from '../../modules/project';
import {
  buildHistoryHeader,
  SIMPLE_ENTITIES
} from '../../static-data/projects';
import PaginationComponent from '../elements/PaginationComponent';

const ITEMS_PER_PAGE = 10;

const getInitials = (user) => {
  if (!user) return '?';
  return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
};

const isLongText = (value) => typeof value === 'string' && value.length > 80;

const isEmpty = (val) =>
  val === '' || val == null || (Array.isArray(val) && val.length === 0);

const formatVal = (val) => (isEmpty(val) ? '-' : formatHistoryValue(val));

const renderFieldChange = (field, oldVal, newVal) => {
  if (isLongText(oldVal) || isLongText(newVal)) {
    return (
      <Box key={field} sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {formatFieldName(field)}
        </Typography>
        <Typography variant="caption" color="text.secondary">Previous</Typography>
        <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
          {formatHistoryValue(oldVal)}
        </Typography>
        <Typography variant="caption" color="text.secondary">New</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {formatHistoryValue(newVal)}
        </Typography>
      </Box>
    );
  }

  if (Array.isArray(oldVal) || Array.isArray(newVal)) {
    const oldList = Array.isArray(oldVal) ? oldVal : [];
    const newList = Array.isArray(newVal) ? newVal : [];

    if (oldList.length === 0) {
      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          <strong>{formatFieldName(field)}</strong> set to {newList.join(', ')}
        </Typography>
      );
    }

    return (
      <Typography key={field} variant="body2" sx={{ mb: 1 }}>
        <strong>{formatFieldName(field)}</strong> changed from{' '}
        {oldList.join(', ')} to {newList.length ? newList.join(', ') : '-'}
      </Typography>
    );
  }

  if (isEmpty(oldVal)) {
    return (
      <Typography key={field} variant="body2" sx={{ mb: 1 }}>
        <strong>{formatFieldName(field)}</strong> set to {formatHistoryValue(newVal)}
      </Typography>
    );
  }

  return (
    <Typography key={field} variant="body2" sx={{ mb: 1 }}>
      <strong>{formatFieldName(field)}</strong> changed from{' '}
      {formatHistoryValue(oldVal)} to {formatHistoryValue(newVal)}
    </Typography>
  );
};

const renderUpdateMessages = (entry) => {
  if (!entry?.changes) return null;

  const { entity_name, changes } = entry;

  if (SIMPLE_ENTITIES.includes(entity_name)) {
    return Object.entries(changes).map(([field, value]) => {
      if (field === 'name') return null;
      const oldVal = value?.old_value;
      const newVal = value?.new_value;
      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          <strong>{formatFieldName(field)}</strong>{' '}
          {isEmpty(oldVal)
            ? <>set to {formatVal(newVal)}</>
            : <>changed from {formatVal(oldVal)} to {formatVal(newVal)}</>}
        </Typography>
      );
    });
  }

  return Object.entries(changes).map(([field, value]) => {
    if (['parent_id', 'parentId', 'parent_name'].includes(field)) return null;

    if (field === 'linked_jira_projects_added' && Array.isArray(value)) {
      return (
        <Box key={field} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Linked Jira projects:
          </Typography>
          {value.map((name, i) => (
            <Typography key={i} variant="body2">• {name}</Typography>
          ))}
        </Box>
      );
    }

    if (field === 'linked_jira_projects_removed' && Array.isArray(value)) {
      return (
        <Box key={field} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Unlinked Jira projects:
          </Typography>
          {value.map((name, i) => (
            <Typography key={i} variant="body2">• {name}</Typography>
          ))}
        </Box>
      );
    }

    const oldVal = value?.old_value;
    const newVal = value?.new_value;

    if (['test_cases_added', 'test_cases_removed'].includes(field)) {
      const action = field === 'test_cases_added' ? 'added' : 'removed';
      if (!Array.isArray(value)) {
        return (
          <Typography key={field} variant="body2">
            {`Test cases ${action}: ${value.count}`}
          </Typography>
        );
      }

      const validTestCases = value.filter(Boolean);
      return (
        <Box key={field} sx={{ mb: 1 }}>
          <Typography variant="body2">
            {`Test case${value.length > 1 ? 's' : ''} ${action}:`}
          </Typography>
          {validTestCases.length > 0 ? (
            validTestCases.map(tc => (
              <Typography key={tc} variant="body2">
                • <strong>{tc}</strong>
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {`${value.length} test case${value.length === 1 ? '' : 's'}`}
            </Typography>
          )}
        </Box>
      );
    }
    if (field === 'mandatory_assigned_to') {
      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          Mandatory assignment of test cases within a test cycle was{' '}
          {newVal ? 'enabled' : 'disabled'}
        </Typography>
      );
    }

    if (field === 'review_workflow') {
      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          Review workflow was {newVal ? 'enabled' : 'disabled'}
        </Typography>
      );
    }

    if (field.endsWith('SlackChannel')) {
      const prefix = field.replace('SlackChannel', '');
      const label = prefix
        ? prefix
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (c) => c.toUpperCase())
          .trim()
        : 'Default';

      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          <strong>{label} Slack Channel</strong>{' '}
          {oldVal && newVal
            ? <>changed from <strong>{oldVal}</strong> to <strong>{newVal}</strong></>
            : oldVal && !newVal
              ? <>removed (<strong>{oldVal}</strong>)</>
              : <>set to <strong>{newVal}</strong></>}
        </Typography>
      );
    }
    return renderFieldChange(field, oldVal, newVal);
  });
};

const HistoryPage = ({ entityType = 'project' }) => {
  const { projectId, suiteId } = useParams();
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});

  const isProject = entityType === 'project';

  const { data = [], isLoading } = useQuery({
    queryKey: isProject
      ? ['project-history', projectId]
      : ['suite-history', projectId, suiteId],
    queryFn: () =>
      isProject
        ? fetchHistory(projectId)
        : fetchHistory(projectId, 'TestSuite', suiteId),
    enabled: isProject ? !!projectId : !!projectId && !!suiteId,
  });

  const { paginatedData, totalPages } = useMemo(() => ({
    paginatedData: data.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    totalPages: Math.ceil(data.length / ITEMS_PER_PAGE),
  }), [data, page]);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <DashboardLayout>
      <Grid
        container
        maxWidth="md"
        sx={{ minHeight: 'calc(100vh - 120px)', flexDirection: 'column' }}
      >
        <Grid item xs={12} mb={3}>
          <Typography variant="h4">
            {isProject ? 'History' : 'Test Suite History'}
          </Typography>
        </Grid>

        {isLoading && (
          <Box textAlign="center">
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && paginatedData.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No history available.
          </Typography>
        )}

        <Box sx={{ flex: 1 }}>
          {!isLoading && paginatedData.map((entry, index) => {
            const showAll = expanded[entry.id];
            const allMessages =
              entry.action_type === 'UPDATE' ? renderUpdateMessages(entry) : [];
            const visibleMessages = showAll ? allMessages : allMessages?.slice(0, 3);

            return (
              <Box key={entry.id} py={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                      {getInitials(entry.performed_by_user)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {entry.performed_by_user?.first_name}{' '}
                      {entry.performed_by_user?.last_name}{' '}
                      {buildHistoryHeader(entry)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {getRelativeTime(entry.created_at)}
                  </Typography>
                </Box>

                <Box pl={5}>
                  {visibleMessages}
                  {allMessages?.length > 3 && (
                    <Button
                      size="small"
                      onClick={() => toggleExpand(entry.id)}
                      sx={{ mt: 0.5 }}>
                      {showAll ? 'Show less' : 'Show more'}
                    </Button>
                  )}
                </Box>

                {index !== paginatedData.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            );
          })}
        </Box>

        {!isLoading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
            <PaginationComponent
              totalCount={data.length}
              currentPage={page}
              handlePageChange={setPage}
              perPage={ITEMS_PER_PAGE}
            />
          </Box>
        )}
      </Grid>
    </DashboardLayout>
  );
};

export default HistoryPage;